// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// types
import {
	Entity,
	EntityLoanPayload,
	EntityPayload,
	ErrorCause,
	Feedback,
	FormField,
	Loan,
	MetadataField,
	MetadataValue,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useEntityForms } from '../../../hooks/useEntityForms';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Checkbox, FormControlLabel, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { DynamicField } from '../../fields/DynamicField/DynamicField';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { translateError } from '../../../utils/translations';

// props
export interface FormAddSupplierProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddSupplier = ({ onClose, onSuccess, setIsLoading }: FormAddSupplierProps) => {
	// hooks
	const { idEntity } = useParams();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();
	const { getDefaultFields } = useEntityForms();

	// params
	const paramDialogSupplierName = searchParams.get('dialogSupplierName');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const idForm = 'form-add-supplier';
	const isWfa = user?.role === 'WFA';
	const userEntityId = userEntity?.id;
	const fieldsDefault = getDefaultFields({ form: 'SUPPLIER' })?.filter((field) =>
		field.name.includes('metadata')
	);

	// state
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [fields, setFields] = useState<FormField[]>(fieldsDefault);
	const [files, setFiles] = useState<File[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [values, setValues] = useState({
		idExternal: '',
		isKycVerified: false,
		loans: [] as string[],
		name: paramDialogSupplierName || '',
		parent: idEntity || userEntityId?.toString() || '',
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(project && !idEntity),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch loans
	let fetchLoansUrl = `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,status`;

	if (isWfa && userEntityId) {
		fetchLoansUrl += `&entity=${userEntityId}`;
	}

	const { fetchRequest: fetchLoans } = useFetch({
		isEnabled: Boolean(project),
		url: fetchLoansUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch form
	const { fetchRequest: fetchForm } = useFetch({
		isEnabled: Boolean(values.parent && project),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${values.parent}&project=${project?.id}&&name=SUPPLIER&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]) {
				setFields([...fieldsDefault, ...res.data[0].fields]);
			}
		},
	});

	// options
	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: String(entity.id),
		};
	});

	const optionsLoans = loans.map((loan) => {
		return {
			disabled: loan.status === 'DISABLED',
			label: loan.code,
			value: String(loan.id),
		};
	});

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// set loader
		setIsLoading(true);

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			// define form data
			const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
			const fd = new FormData(form);
			const entries = Object.fromEntries(fd.entries());

			// set parent entity
			const idParent = idEntity ? Number(idEntity) : Number(values.parent);

			// construct loans array
			const loans: EntityLoanPayload[] = [];

			// create relationship with same parent for each loan selected
			for (let i = 0; i < values.loans.length; i++) {
				loans.push({
					id: Number(values.loans[i]),
					parent: idParent,
				});
			}

			// define body
			const body: Partial<EntityPayload> = {
				isKycVerified: values.isKycVerified,
				loans,
				name: values.name,
				project: project?.id,
				type: 'SUPPLIER',
			};

			const bodyMetadata = [] as MetadataField[];

			// construct body using fields and entries
			for (let i = 0; i < fields.length; i++) {
				const field = fields[i];

				let value: MetadataValue = entries[field.name] ? String(entries[field.name]) : null;

				if (field.type === 'CHECKBOX') {
					value = Boolean(value);
				}

				if (field.type === 'NUMBER') {
					value = Number(value);
				}

				let name = field.name;
				if (field.name.includes('metadata-')) {
					name = field.name.replace('metadata-', '');
				}

				bodyMetadata.push({
					isProtected: Boolean(field.isProtected),
					label: field.label,
					name,
					type: field.type,
					value,
				});
			}
			body.metadata = bodyMetadata;

			if (values.idExternal) {
				body.idExternal = values.idExternal;
			}

			const fetchAddSupplier = await fetch(`${process.env.REACT_APP_API_URL}/entities`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddSupplier = await fetchAddSupplier.json();

			if (resAddSupplier.error) {
				throw new Error(resAddSupplier.error.message, {
					cause: {
						details: resAddSupplier.error?.details,
						id: resAddSupplier.error.id,
					},
				});
			}

			const supplier = resAddSupplier.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/suppliers/${supplier.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('entities[]', String(supplier.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({
						alert: {
							severity: 'error',
						},
						response: resPostDoc,
					});
				}
			}

			const bannerMessage = t('alert.supplierCreated', { name: values.name });
			feedbackNew.items.push({
				alert: {
					children: bannerMessage,
					severity: 'success',
				},
			});

			if (onSuccess) {
				onSuccess();
			}
			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				// set success banner
				setBanner({
					hasClose: true,
					message: bannerMessage,
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause) {
				if (cause.id === 'ENTITY_DUPLICATE' || cause.id === 'ENTITY_DUPLICATE_ID_EXTERNAL') {
					cause.details.idExternal = values.idExternal;
				}
			}

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t, frame: 'SUPPLIER' }),
					severity: 'error',
				},
			});

			// set error
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// fetch form and loans when parent entity changes
	useEffect(() => {
		if (values.parent) {
			fetchForm();
			fetchLoans();
		}
	}, [values.parent, fetchForm, fetchLoans]);

	return (
		<form id={idForm} name="formAddSupplier" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						helperText={t('idExternal.helper')}
						id={`${idForm}-id-external`}
						label={t('uniqueIdentifier')}
						name="idExternal"
						onChange={handleChangeValue}
						required={true}
						value={values.idExternal}
					/>
				</Grid>

				{!idEntity && Boolean(optionsEntities.length) && (
					<Grid item xs={6}>
						<Select
							id={`${idForm}-parent`}
							label={t('entity')}
							name="parent"
							onChange={handleChangeValue}
							options={optionsEntities}
							required={true}
							value={values.parent}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={idEntity ? 12 : 6}>
					<Select
						hasAllOption={true}
						id={`${idForm}-loans`}
						label={t('financingSources')}
						multiple={true}
						name="loans"
						onChange={handleChangeValue}
						options={optionsLoans}
						required={true}
						value={values.loans}
					/>
				</Grid>
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setFiles(filesSelected);
						}}
					/>
				</Grid>
				{fields.map((field, i) => {
					return (
						<Grid
							alignSelf={field.type === 'CHECKBOX' ? 'flex-end' : 'flex-start'}
							item
							key={`field-${field.name}-${i}`}
							lg={6}
							xs={12}>
							<DynamicField field={field} />
						</Grid>
					);
				})}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={values.isKycVerified}
								id={`${idForm}-is-kyc-verified`}
								name="isKycVerified"
								onChange={(e) => setValues({ ...values, isKycVerified: e.target.checked })}
								required={true}
							/>
						}
						label={t('formAddSupplier.kycAml')}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
